import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';
import { defaultSettings } from '../pages/admin/settings/settingsData';
import { fetchGymSettings, saveGymSettings } from '../services/gym/gymSettingsApi';
import {
  formatGymCompact,
  formatGymPrice,
  getGymSettings,
  setGymSettingsCache,
} from '../utils/gymMoney';
import { setInvoiceSequenceStart } from '../services/receptionist/invoiceSequence';

const GymSettingsContext = createContext(null);

export function GymSettingsProvider({ children }) {
  const { account } = useAuth();
  const gymId = account?.gym_id || '';
  const [settings, setSettings] = useState(() => getGymSettings());
  const [loading, setLoading] = useState(Boolean(gymId));
  const [error, setError] = useState('');

  const applySettings = useCallback((next) => {
    const cached = setGymSettingsCache(next);
    setSettings(cached);
    const crStart = Number(cached.invoiceSequenceStart);
    if (gymId && Number.isFinite(crStart) && crStart > 0) {
      try {
        setInvoiceSequenceStart(crStart, gymId);
      } catch {
        /* ignore invalid */
      }
    }
    return cached;
  }, [gymId]);

  const refresh = useCallback(async () => {
    if (!gymId) {
      applySettings(defaultSettings);
      setLoading(false);
      return defaultSettings;
    }

    try {
      setLoading(true);
      setError('');
      const data = await fetchGymSettings(gymId);
      applySettings(data);
      return data;
    } catch (err) {
      setError(err.message || 'Failed to load gym settings');
      applySettings(defaultSettings);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [gymId, applySettings]);

  useEffect(() => {
    refresh().catch(() => {});
  }, [refresh]);

  const saveSettings = useCallback(async (next) => {
    if (!gymId) throw new Error('Gym context is missing. Sign in again.');
    const saved = await saveGymSettings(gymId, next);
    return applySettings(saved);
  }, [gymId, applySettings]);

  const formatPrice = useCallback(
    (amount) => formatGymPrice(amount, settings),
    [settings],
  );

  const formatCompact = useCallback(
    (amount) => formatGymCompact(amount, settings),
    [settings],
  );

  const value = useMemo(() => ({
    settings,
    loading,
    error,
    gymId,
    refresh,
    saveSettings,
    applySettings,
    formatPrice,
    formatCompact,
    currency: settings.currency,
    gym: settings.gym,
  }), [settings, loading, error, gymId, refresh, saveSettings, applySettings, formatPrice, formatCompact]);

  return (
    <GymSettingsContext.Provider value={value}>
      {children}
    </GymSettingsContext.Provider>
  );
}

export function useGymSettings() {
  const context = useContext(GymSettingsContext);
  if (!context) {
    throw new Error('useGymSettings must be used within GymSettingsProvider');
  }
  return context;
}
